import { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useListContext } from 'react-admin';

/**
 * Like FilterLiveSearch, but does not call setFilters on mount.
 * FilterLiveForm + TextInput can fire an empty `q` change when the aside mounts
 * (after the first page fetch), and setFilters always resets page to 1.
 */
export const SidebarLiveSearch = ({ placeholder }: { placeholder: string }) => {
    const { filterValues, setFilters, displayedFilters } = useListContext();
    const currentQ = typeof filterValues.q === 'string' ? filterValues.q : '';
    const [value, setValue] = useState(currentQ);
    const appliedQ = useRef(currentQ);
    const filterValuesRef = useRef(filterValues);
    const displayedFiltersRef = useRef(displayedFilters);
    filterValuesRef.current = filterValues;
    displayedFiltersRef.current = displayedFilters;

    useEffect(() => {
        setValue(currentQ);
        appliedQ.current = currentQ;
    }, [currentQ]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            const next = value.trim();
            if (next === appliedQ.current) return;
            appliedQ.current = next;
            const { q: _q, ...rest } = filterValuesRef.current;
            setFilters(
                next ? { ...rest, q: next } : rest,
                displayedFiltersRef.current,
                false,
            );
        }, 500);
        return () => window.clearTimeout(handle);
    }, [value, setFilters]);

    const clearSearch = () => {
        setValue('');
        if (appliedQ.current === '') return;
        appliedQ.current = '';
        const { q: _q, ...rest } = filterValuesRef.current;
        setFilters(rest, displayedFiltersRef.current, false);
    };

    return (
        <TextField
            hiddenLabel
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={placeholder}
            size="small"
            variant="filled"
            fullWidth
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {value ? (
                            <IconButton
                                size="small"
                                aria-label="Clear search"
                                onClick={clearSearch}
                                onMouseDown={e => e.preventDefault()}
                                edge="end"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        ) : (
                            <SearchIcon color="disabled" />
                        )}
                    </InputAdornment>
                ),
            }}
            sx={{
                display: 'block',
                '& .MuiFilledInput-root': {
                    width: '100%',
                    // Match CreateButton touch sizing on coarse pointers (iOS Safari).
                    '@media (hover: none) and (pointer: coarse)': {
                        minHeight: 48,
                    },
                },
            }}
        />
    );
};
